import omit from 'lodash/omit';

import { type DocumentChunk, type DocumentChunkContent, DocumentChunkType } from '../../types/chunkedDocuments';
import { arrayBufferToUTF8String } from '../../utils/arrayBufferToUTF8String';
// eslint-disable-next-line import/no-cycle
import { decryptPassword } from '../../utils/deviceCrypto.platform';
// eslint-disable-next-line import/no-cycle
import exceptionHandler from '../../utils/exceptionHandler.platform';
import { globalState } from '../models';
import background from '../portalGates/toBackground';
import { base64ToArrayBuffer } from './bytes';
import { decryptData } from './drm';

function formatErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function getProfileId(): number {
  const profileId = globalState.getState().client.profile?.profile_id;
  if (!profileId) {
    throw new Error('No profile ID available for decryption');
  }
  return profileId;
}

async function getDecryptedPassword(chunk: DocumentChunk, docId?: string): Promise<string | undefined> {
  if (!chunk.ep) {
    return chunk.password ?? undefined;
  }

  const profileId = getProfileId();

  try {
    return await decryptPassword(chunk.ep, profileId);
  } catch (error) {
    exceptionHandler.captureException('RSA decryption of chunk password failed, attempting recovery', {
      extra: {
        chunk: omit(chunk, 'data', 'password', 'ep'),
        profileId,
        profileIdType: typeof profileId,
        error: formatErrorMessage(error),
      },
    });
  }

  await background.forceReRegisterWithCacheClear(profileId);
  const redownloadedMap = await background.forceRedownloadChunksWithIds([chunk.id], docId);
  const redownloadedChunk = redownloadedMap[chunk.id];
  if (redownloadedChunk) {
    Object.assign(chunk, redownloadedChunk);
  }

  if (!chunk.ep) {
    return chunk.password ?? undefined;
  }

  try {
    return await decryptPassword(chunk.ep, profileId);
  } catch (retryError) {
    exceptionHandler.captureException('RSA decryption of chunk password failed after recovery', {
      extra: {
        chunk: omit(chunk, 'data', 'password', 'ep'),
        profileId,
        profileIdType: typeof profileId,
        error: formatErrorMessage(retryError),
      },
    });
    return chunk.password ?? undefined;
  }
}

export async function unpackChunk(chunk: DocumentChunk, docId?: string): Promise<DocumentChunkContent | undefined> {
  const data = base64ToArrayBuffer(chunk.data);
  if (!chunk.is_encrypted) {
    // Explanation: base64ToArrayBuffer returns ArrayBufferLike but decodeChunkContent expects ArrayBuffer — safe at runtime
    return decodeChunkContent(chunk, data as ArrayBuffer);
  }
  let password = await getDecryptedPassword(chunk, docId);
  if (!password) {
    exceptionHandler.captureException('Chunk is encrypted but no password, redownloading', {
      extra: {
        chunk: omit(chunk, 'data', 'password', 'ep'),
      },
    });
    const redownloadedMap = await background.forceRedownloadChunksWithIds([chunk.id], docId);
    const redownloadedChunk = redownloadedMap[chunk.id];
    if (redownloadedChunk) {
      Object.assign(chunk, redownloadedChunk);
    }
    password = await getDecryptedPassword(chunk, docId);
  }
  if (!password) {
    exceptionHandler.captureException('Chunk is encrypted but no password, even after redownload, bailing', {
      extra: {
        chunk: omit(chunk, 'data', 'password', 'ep'),
      },
    });
    return undefined;
  }
  const decryptedData = await decryptData(data, password);
  return decodeChunkContent(chunk, decryptedData);
}

function decodeChunkContent(chunk: DocumentChunk, data: ArrayBuffer): DocumentChunkContent | undefined {
  switch (chunk.type) {
    case DocumentChunkType.Style:
    case DocumentChunkType.Script:
    case DocumentChunkType.Navigation:
    case DocumentChunkType.Document:
    case DocumentChunkType.Smil:
      return {
        type: chunk.type,
        id: chunk.internal_id,
        text: arrayBufferToUTF8String(data),
        data: undefined,
      };
    case DocumentChunkType.Image:
    case DocumentChunkType.Unknown:
    case DocumentChunkType.Vector:
    case DocumentChunkType.Font:
    case DocumentChunkType.Video:
    case DocumentChunkType.Audio:
    case DocumentChunkType.Cover:
      return {
        type: chunk.type,
        id: chunk.internal_id,
        text: undefined,
        data,
      };
  }
}
